import { coinFlip } from '@/lib/coinFlip'
import { Part as PartType } from '@/types/part'
import { PartGroup as PartGroupType } from '@/types/partGroup'
import {
  AspectRatio,
  Box,
  Button,
  Container,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import React from 'react'
import TextBlock from '../contentBlocks/TextBlock'
import { VideoBlock } from '../contentBlocks/VideoBlock'
import { QuoteMarkIcon } from '../icons/QuoteMarkIcon'
import { BuilderQAndA } from './BuilderQAndA'
import { MemberSpotlightArticle as MemberSpotlightArticleType } from './MemberSpotlightArticle.types'
import { SpotlightGallery } from './SpotlightGallery'
import { getVideoId } from '@/lib/youtube/getVideoId'

type MemberSpotlightArticleProps = {
  entry: MemberSpotlightArticleType
  partsList?: PartGroupType[]
}

export const MemberSpotlightArticle = (props: MemberSpotlightArticleProps) => {
  if (!props.entry) return null

  const getPreviousBlockType = (i: number) => {
    if (i === 0) return null
    const prev = props.entry.build_story[i - 1]
    if (!prev) return null
    if ('text' in prev) return 'text'
    if ('quote' in prev) return 'quote'
    if ('image' in prev) return 'image'
    if ('video' in prev) return 'video'
  }

  return (
    <>
      <style>
        {`
          .drop-cap:first-child:first-letter {
            float: left;
            font-size: 120px;
            line-height: 90px;
            padding-top: 0px;
            padding-right: 8px;
            padding-left: 3px;
            color: var(--chakra-colors-blue-800);
          }
          .drop-cap:first-line {
            font-weight: bold;
            color: var(--chakra-colors-blue-800);
          }
        `}
      </style>
      <Stack alignItems='center' py={20} gap={12}>
        {props.entry.build_story?.length > 0
          ? props.entry.build_story.map((block, i) => (
              <Box w='full' my={8} key={i}>
                {'text' in block && (
                  <Container maxW='3xl'>
                    <div className={i === 0 ? 'drop-cap' : ''}>
                      <TextBlock text={block.text} textAlign='left' />
                    </div>
                  </Container>
                )}
                {'quote' in block && (
                  <Container maxW='3xl'>
                    <Box position='relative'>
                      <Box
                        backgroundColor='gray.150'
                        borderColor='gray.200'
                        borderRadius='lg'
                        borderWidth={2}
                        color='red.400'
                        height='84px'
                        left={{ base: -4, lg: -2 }}
                        pos='absolute'
                        py={3}
                        textShadow='0 0 0 rgba(255,255,255)'
                        top='50%'
                        transform='scale(.625) translateY(-75%)'>
                        <div style={{ transform: 'translateY(-25%)' }}>
                          <QuoteMarkIcon fill='red.400' />
                        </div>
                      </Box>
                      <Text
                        as='blockquote'
                        fontSize={{ base: 'xl', lg: '32px' }}
                        fontWeight='900'
                        lineHeight='1.3'
                        my={8}
                        ml={12}
                        pl={{ base: 8, lg: 12 }}
                        py={4}
                        borderLeftWidth={4}
                        borderColor='gray.200'
                        style={
                          {
                            textWrap: 'balance',
                            backgroundImage:
                              "url('/img/dark-background-texture.webp')",
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            backgroundAttachment: 'fixed',
                            WebkitTextFillColor: 'transparent',
                          } as React.CSSProperties
                        }>
                        {block.quote}
                      </Text>
                    </Box>
                  </Container>
                )}
                {'image' in block && (
                  <Container maxW='3xl'>
                    <Box pt={getPreviousBlockType(i) !== 'image' ? 12 : 0}>
                      <AspectRatio ratio={16 / 9} backgroundColor='gray.100'>
                        <img
                          alt={block.image[0]?.alt ?? ''}
                          src={block.image[0]?.url}
                          width='100%'
                          height='100%'
                          style={{ objectFit: 'cover' }}
                        />
                      </AspectRatio>
                      {block.caption && (
                        <Box
                          px={6}
                          pos='relative'
                          textAlign='center'
                          width='100%'
                          transform='translateY(-50%)'
                          position='relative'>
                          <img
                            src='/img/plus.webp'
                            alt=''
                            style={{
                              position: 'absolute',
                              width: '120px',
                              height: '120px',
                              top: 'calc(50% - 20px)',
                              right: '-10px',
                              transform: 'translateY(-50%)',
                            }}
                          />
                          <Box
                            backgroundColor='gray.700'
                            color='white'
                            p={2}
                            pos='relative'
                            zIndex='1'>
                            <Text
                              as='div'
                              fontSize={{ base: 'xs', md: 'sm' }}
                              style={
                                { textWrap: 'balance' } as React.CSSProperties
                              }>
                              {block.caption}
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Container>
                )}
                {'video' in block && (
                  <VideoBlock
                    title=''
                    video={`https://www.youtube.com/embed/${getVideoId(
                      block.video
                    )}`}
                  />
                )}
              </Box>
            ))
          : null}
        <BuilderQAndA content={props.entry.replicator_field} />
        <div
          style={{ overflow: 'hidden', width: '100%', paddingBottom: '60px' }}>
          <Container maxW='4xl'>
            <Box
              p={2}
              opacity={0.125}
              pos='relative'
              left='-12px'
              style={{ rotate: '-4deg', transformOrigin: 'left bottom' }}>
              <Heading fontSize='7xl'>Parts List</Heading>
            </Box>
            <Box
              boxShadow='xl'
              px={4}
              py={6}
              pos='relative'
              style={{
                backgroundImage: 'url(/img/lined-blue-paper-texture.webp)',
                width: '100%',
                backgroundSize: '1200px',
                backgroundRepeat: 'repeat-y',
                backgroundPosition: 'center',
              }}>
              <img
                src='/img/coffee-stain.webp'
                alt=''
                style={{
                  mixBlendMode: 'multiply',
                  opacity: 0.2,
                  position: 'absolute',
                  width: '270px',
                  height: '270px',
                  bottom: '-80px',
                  right: '-80px',
                }}
              />
              {props.partsList && (
                <Box>
                  {props.entry.parts_list_note && (
                    <Text as='div' fontSize={{ base: 'xs', md: 'sm' }} p={8}>
                      {props.entry.parts_list_note}
                    </Text>
                  )}
                  <SimpleGrid columns={{ base: 1, lg: 2 }}>
                    {props.partsList.map((group: PartGroupType) => (
                      <PartGroup key={group.type} group={group} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </Box>
          </Container>
        </div>
        <SpotlightGallery images={props.entry.gallery} />
      </Stack>

      {props.entry.original_article && (
        <Box pb={24} pt={{ base: 0, md: 12 }}>
          <Container>
            <NextLink
              href={props.entry.original_article?.replace(
                'https://debossgarage.com',
                ''
              )}>
              <Stack
                textAlign='center'
                gap={4}
                backgroundColor='gray.100'
                p={8}>
                <Heading>Comment & Like this Project</Heading>
                <div>
                  <Button colorScheme='red'>View Project</Button>
                </div>
              </Stack>
            </NextLink>
          </Container>
        </Box>
      )}
    </>
  )
}

const PartGroup = ({ group }: { group: PartGroupType }) => {
  const layouts = {
    namedGroupLayout: {
      columns: 1,
      gridColumnStart: undefined,
      gridColumnEnd: undefined,
      width: undefined,
    },
    unnamedGroupLayout: {
      columns: 2,
      gridColumnStart: 1,
      gridColumnEnd: '-1',
      width: '100%',
    },
  }

  const layoutName = group.name ? 'namedGroupLayout' : 'unnamedGroupLayout'

  return (
    <SimpleGrid
      key={group.id}
      alignItems='flex-start'
      p={2}
      gap={2}
      columns={layouts[layoutName].columns}
      width={layouts[layoutName].width}
      ml={4}
      gridColumnStart={layouts[layoutName].gridColumnStart}
      gridColumnEnd={layouts[layoutName].gridColumnEnd}
      transform={`rotate(${coinFlip('-', '')}${Math.floor(
        Math.random() * 1
      )}deg)`}>
      {group.parts &&
        group.parts.length > 0 &&
        group.parts.map((part: PartType, i) => (
          <Part
            key={part.id}
            part={part}
            groupName={i === 0 && group.name ? group.name : null}
          />
        ))}
    </SimpleGrid>
  )
}

const PartGroupName: React.FC<{ name: string | null }> = props => {
  if (!props.name) return null

  return (
    <Text
      maxWidth='fit-content'
      fontFamily='Caveat'
      textTransform='uppercase'
      lineHeight={1}
      color='blue.500'
      fontWeight='bold'
      fontSize='sm'
      letterSpacing='wide'
      transform={`rotate(${coinFlip('-', '')}${Math.floor(
        Math.random() * 3.5
      )}deg)`}
      top='8px'
      position='relative'
      marginBottom='0'>
      {props.name}
    </Text>
  )
}

const Part = ({
  part,
  groupName,
}: {
  part: PartType
  groupName: string | null
}) => {
  return (
    <Box key={part.id}>
      <PartGroupName name={groupName} />
      <div>
        <Text
          as='div'
          fontWeight='bold'
          fontSize='xl'
          fontFamily='Caveat'
          color='blue.700'
          lineHeight={1}
          mb={1}
          mt={3}>
          {part.name}
        </Text>
        {part.number && (
          <Text
            as='div'
            borderRadius='base'
            color='blue.700'
            fontSize='2xs'
            fontWeight='bold'
            width='fit-content'>
            Part No. {part.number}
          </Text>
        )}
      </div>
      {part.productUrl && (
        <Link
          color='blue.700'
          fontSize='xs'
          href={part.productUrl}
          target='_blank'
          rel='noreferrer nofollow noopener'>
          visit product site ›
        </Link>
      )}
    </Box>
  )
}
